import { Button as RACButton } from "react-aria-components";
import { Icon, IconCheck } from "../icon";
import { cx } from "../tv";

export type Step = { id: string; label: string };

/** Connector between steps: a hairline on desktop, a short vertical rule on phones. Accent once the step before it is done. */
const connector = (done: boolean) =>
  cx(
    "mx-2 h-px min-w-6 flex-1 rounded-pill max-tablet:mx-0 max-tablet:ms-3 max-tablet:my-1 max-tablet:h-6 max-tablet:w-px max-tablet:min-w-0 max-tablet:flex-none",
    done ? "bg-accent" : "bg-border",
  );

/** The number circle: a check when done, the accent ring when current, a quiet ring when still to come (DESIGN-RULES 9). */
function Badge({ state, index }: { state: "done" | "current" | "todo"; index: number }) {
  return (
    <span
      aria-hidden
      className={cx(
        "tabular grid size-6 shrink-0 place-items-center rounded-pill text-xs font-semibold",
        state === "done" && "bg-accent text-accent-fg",
        state === "current" && "bg-accent-subtle text-accent shadow-[inset_0_0_0_1px_var(--accent)]",
        state === "todo" && "bg-panel-solid text-fg-muted shadow-[inset_0_0_0_1px_var(--border-strong)]",
      )}
    >
      {state === "done" ? <Icon icon={IconCheck} size="chip" /> : index + 1}
    </span>
  );
}

/**
 * Horizontal steps with number circles. Done steps show a check, the current step is accented, and below 768 px the
 * steps stack vertically. `current` is the id of the current step; `onStepPress` makes every step a button.
 */
export function Stepper({ steps, current, onStepPress, ariaLabel, doneLabel, className }: {
  steps: Step[];
  /** Id of the current step. Steps before it are done. */
  current: string;
  /** Called with the step id when a step is pressed. Without it, the steps are read-only. */
  onStepPress?: (id: string) => void;
  /** Name of the whole list for screen readers. */
  ariaLabel: string;
  /** Screen-reader word for a finished step, read after its label. */
  doneLabel: string;
  className?: string;
}) {
  const currentIndex = steps.findIndex((s) => s.id === current);
  return (
    <ol aria-label={ariaLabel} className={cx("flex w-full min-w-0 max-tablet:flex-col", className)}>
      {steps.map((step, i) => {
        const state = i < currentIndex ? "done" : i === currentIndex ? "current" : "todo";
        const body = (
          <>
            <Badge state={state} index={i} />
            <span className={cx("truncate-1 min-w-0 text-sm", state === "current" ? "font-semibold text-fg" : "font-medium text-fg-muted")} title={step.label}>
              {step.label}
              {state === "done" && <span className="sr-only"> ({doneLabel})</span>}
            </span>
          </>
        );
        const base = cx(
          "flex min-w-0 max-w-full items-center gap-2 rounded-md px-1 py-1 outline-none max-tablet:min-h-11",
          "transition-[translate,scale,box-shadow,background-color,color] duration-(--dur-fast) ease-out",
          "focus-visible:shadow-focus pressed:scale-[.98]",
          onStepPress && "hovered:bg-hover hovered:text-fg",
        );
        return (
          <li key={step.id} className={cx("flex min-w-0 items-center max-tablet:w-full max-tablet:flex-col max-tablet:items-start", i === steps.length - 1 ? "flex-none" : "flex-1")}>
            {onStepPress ? (
              <RACButton aria-current={state === "current" ? "step" : undefined} onPress={() => onStepPress(step.id)} className={base}>
                {body}
              </RACButton>
            ) : (
              <span aria-current={state === "current" ? "step" : undefined} className={base}>
                {body}
              </span>
            )}
            {i < steps.length - 1 && <span aria-hidden className={connector(i < currentIndex)} />}
          </li>
        );
      })}
    </ol>
  );
}
